// @flow
import appendUrlParams from '@rover/utilities/appendUrlParams';
import type { UtmParams } from '@rover/react-lib/types';
import { snakeify } from '@rover/utilities/object-utilities';
import { addUtmParamsToUrl } from '@rover/utilities/utm';

import getUrlParameter from './getUrlParameter';
import { getCookie } from './cookie';

// Appends the URL of the first http referrer for the given session, used for attribution
export const appendFirstHttpReferrer = (deepLinkWithParams: string, firstHttpReferrer: string) => {
  if (!firstHttpReferrer) return deepLinkWithParams;
  return appendUrlParams(deepLinkWithParams, {
    http_referrer: encodeURIComponent(firstHttpReferrer),
  });
};

// Appends UTM tracking data for Ambassador referral tracking: https://www.getambassador.com/
export const appendAmbassador = (deepLinkWithParams: string) => {
  const ambassadorMbsyCode = getUrlParameter('mbsy');
  if (!ambassadorMbsyCode) return deepLinkWithParams;

  const promoCode = getUrlParameter('promo_code');
  const utmSource = getUrlParameter('utm_source');
  const utmContent = getUrlParameter('utm_content');

  const extraAmbassadorParams = {
    mbsy: ambassadorMbsyCode,
    campaignid: getUrlParameter('campaignid') || '',
    ...(promoCode && { promoCode }),
    ...(utmSource && { utmSource }),
    ...(utmContent && { utmContent }),
  };

  return appendUrlParams(deepLinkWithParams, snakeify(extraAmbassadorParams));
};

// Appends the rover_cid, used in analytics to uniquely identify a device across sessions
export const appendRoverCid = (deepLinkWithParams: string) => {
  const roverCid = getCookie('rover_cid');
  if (!roverCid) return deepLinkWithParams;
  return appendUrlParams(deepLinkWithParams, { rover_cid: roverCid });
};

// Appends the Alooma ID, used by Alooma to uniquely identify a device across sessions
export const appendAloomaDistinctId = (deepLinkWithParams: string) => {
  const aloomaCookie = getCookie('mp_rover_alooma');

  if (aloomaCookie) {
    const aloomaDistinctId = JSON.parse(decodeURIComponent(`${aloomaCookie}`)).distinct_id;

    if (aloomaDistinctId) {
      return appendUrlParams(deepLinkWithParams, { distinct_id: aloomaDistinctId });
    }
  }

  return deepLinkWithParams;
};

// Generates a URL used for Branch.IO deeplinking: https://branch.io/
export const generateRouteUrl = (deepLinkWithParams: string, branchKey: string) => {
  const afterClickUrl = encodeURIComponent(window.location.href);
  const deeplinkPath = encodeURIComponent(deepLinkWithParams.split('rover://')[1]);
  return appendUrlParams(`https://r.rover.com/a/${branchKey}`, {
    $deeplink_path: deeplinkPath,
    $after_click_url: afterClickUrl,
  });
};

// A function for getting an app deep link URL, without dependencies on window globals
export const getRouteUrlContextless = (
  deepLink: string = 'rover://campaign/',
  utmParams: UtmParams,
  firstHttpReferrer: string,
  branchKey: string
) => {
  let deepLinkWithParams = deepLink;
  deepLinkWithParams = addUtmParamsToUrl(utmParams, deepLinkWithParams);
  deepLinkWithParams = appendFirstHttpReferrer(deepLinkWithParams, firstHttpReferrer);
  deepLinkWithParams = appendAmbassador(deepLinkWithParams);
  deepLinkWithParams = appendRoverCid(deepLinkWithParams);
  deepLinkWithParams = appendAloomaDistinctId(deepLinkWithParams);

  return generateRouteUrl(deepLinkWithParams, branchKey);
};

// A function for getting an app deep link URL, with dependencies on window globals
export const getRouteURL = (deepLink: string = 'rover://campaign/') => {
  const {
    deeplink: roverDeepLink,
    context: { branchKey },
    UTMParameters: utmParams,
    firstHttpReferrer,
  } = window.Rover;

  return getRouteUrlContextless(roverDeepLink || deepLink, utmParams, firstHttpReferrer, branchKey);
};

export default getRouteUrlContextless;



// WEBPACK FOOTER //
// ../utilities/deeplink.js